import { useState, useRef, useEffect } from 'react';
import { useRouter } from 'next/router';
import { t } from 'i18next';
// components
import { Checkbox, Form, IconButton, Input, InputGroup, Panel, Toggle } from 'rsuite';
import FormInputPicker from '@commons/EXComponentsLibrary/FormInputPicker/FormInputPicker';
import { InformationModal, Layouts } from '@commons/components';
import { ExButton, ExMultiSelectionChecked, useToast } from '@commons/EXComponentsLibrary';
import DatePickerForm from '../InitialConfig/Agreements/AgreementForm/DatePickerForm/DatePickerForm';
// hooks & api
import {
    agreementsValidate,
    deleteAgreementById,
    getCommercialAgreementById,
    saveAgreementsCustom,
    updateAgreements,
} from 'api/api-agreements';
// utils && hooks
import {
    useDrugManufacturersByClientIdWithoutPagination,
    useDrugManufacturersConnectedByClientId,
    useUser,
} from 'utils/hooks';
// contexts
import { useAlert } from 'context';
// styles
import styles from './AgreementForm.module.css';
import dayjs from 'dayjs';

const AgreementForm = () => {
    enum WarningType {
        REPEATED_PRODUCTS = 'repeated_products',
        PREVIOUS_AGREEMENTS = 'previous_agreements',
    }
    interface Warning {
        id: number;
        name: WarningType;
    }
    const {
        query: { clientId, id },
        pathname,
        push,
    } = useRouter();

    const [formValues, setFormValues] = useState({
        id: null,
        drugManufacturerName: { name: '', id: null },
        selectedPharmacy: [],
        all: [],
        lists: [],
        isVisiblePercentage: true,
        isEditMode: false,
    });
    const [showModal, setShowModal] = useState(false);
    const [warnings, setWarnings] = useState<Warning[]>([]);
    const [loadingFetching, setLoadingFetching] = useState(false);
    const [hasChanged, setHasChanged] = useState(false);

    const haveIds = formValues.isEditMode ? !!id : !id;

    const { drugManufacturers: allDrugManufacturers, isValidating } = useDrugManufacturersConnectedByClientId(
        clientId,
        false,
        haveIds,
    );
    const { show } = useAlert();
    const toaster = useToast();
    const { user } = useUser({});
    const pos = user?.pointsOfSale.map(({ name, id }) => ({ label: name, value: id })) || [];
    const formattedTest: any = allDrugManufacturers?.data?.map(({ name, id }) => ({
        label: name,
        value: id,
    }));

    const items = [
        { id: 1, label: 'Todos los productos medicinales' },
        { id: 2, label: 'Todos los productos NO medicinales' },
        { id: 4, label: 'Todos los productos refrigerados' },
    ];

    const fileInputRef = useRef<HTMLInputElement>(null);
    const pendingCheckboxRef = useRef<number | null>(null);

    const fetchCommercialAgreement = async (id: string) => {
        const resp = await getCommercialAgreementById(id);
        const d = resp.data.data;

        // Mapeo de puntos de venta
        const selectedPharmacy = d.points_of_sale.map((ps: any) => ps.id);

        // Mapeo de los items “all” (type_id 1,2,4)
        const all = d.lists
            .filter((l: any) => [1, 2, 4].includes(l.type.id))
            .map((l: any) => ({
                id: l.id,
                type_id: l.type.id,
                percentage: parseFloat(l.percentage),
                expires_at: l.expires_at, // YYYY-MM-DD
                status: l.status.name, // “expired” o “active”, etc.
                isExpired: l.status.name === 'expired', // booleano para facilitar lógica en el UI
            }));

        // Mapeo de los listados personalizados (type_id 3)
        const lists = d.lists
            .filter((l: any) => l.type.id === 3)
            .map((l: any) => ({
                id: l.id,
                file: l.filename, // no podemos precargar el File object
                name: l.name || '',
                percentage: parseFloat(l.percentage),
                expires_at: l.expires_at,
                status: l.status.name,
                isExpired: l.status.name === 'expired',
            }));

        setFormValues({
            id: d.id,
            drugManufacturerName: {
                id: d.drug_manufacturer.id,
                name: d.drug_manufacturer.name,
            },
            selectedPharmacy,
            all,
            lists,
            isVisiblePercentage: d.visible_percentage === 'true' || d.visible_percentage === '1',
            isEditMode: true,
        });
    };

    const handleCheckboxChange = (id: number) => {
        setFormValues((prev) => {
            const exists = prev.all.some((item) => item.type_id === id);
            const updatedAll = exists
                ? prev.all.filter((item) => item.type_id !== id)
                : [
                      ...prev.all,
                      {
                          type_id: id,
                          percentage: null,
                          expires_at: null,
                      },
                  ];
            // Si deselecciono el listado personalizado (id=3), elimino el file asociado
            const updatedLists = id === 3 && exists ? prev.lists.filter((entry) => entry.checkbox !== 3) : prev.lists;
            return {
                ...prev,
                all: updatedAll,
                lists: updatedLists,
            };
        });
        setHasChanged(true);
    };

    const handleAddCustomList = () => {
        const newId = Date.now();
        pendingCheckboxRef.current = newId;
        fileInputRef.current?.click();
    };

    const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const file = e.target.files?.[0];
        const id = pendingCheckboxRef.current;
        if (!file || id === null) return;

        // validación de extensiones/mime
        const exts = ['.txt', '.csv', '.xlsx'];
        const mime = [
            'text/plain',
            'text/csv',
            'application/vnd.ms-excel',
            'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
        ];
        if (!exts.some((ext) => file.name.toLowerCase().endsWith(ext)) || !mime.includes(file.type)) {
            alert('Solo .txt, .csv o .xlsx');
            pendingCheckboxRef.current = null;
            return;
        }

        const newEntry = {
            id: Date.now(),
            checkbox: 3, // ← para mantener tu lógica posterior
            file,
            name: '',
            percentage: null,
            expires_at: null,
            status: 'active',
            isExpired: false,
        };

        setFormValues((prev) => ({
            ...prev,
            lists: [...prev.lists, newEntry],
        }));

        setHasChanged(true);

        pendingCheckboxRef.current = null;
        e.target.value = '';
    };

    const handleValidation = async () => {
        // 1) Construir FormData
        const formData = new FormData();
        formData.append('client_id', clientId as string);
        formData.append('drug_manufacturer_id', formValues.drugManufacturerName.id as string);
        formValues.selectedPharmacy.forEach((id: number) => formData.append('point_of_sale_ids[]', id.toString()));

        // Enviar booleano como "1" / "0"
        formData.append('visible_percentage', formValues.isVisiblePercentage ? '1' : '0');

        // 2) Campos "all", filtrando fuera el type_id=3
        formValues.all
            .filter((item: any) => item.type_id !== 3)
            .forEach((item: any, idx: number) => {
                formData.append(`all[${idx}][type_id]`, item.type_id.toString());
                if (item.percentage != null) {
                    formData.append(`all[${idx}][percentage]`, item.percentage.toString());
                }
                if (item.expires_at) {
                    formData.append(`all[${idx}][expires_at]`, item.expires_at);
                }
            });

        // 3) Campos "lists" (incluyendo el File)
        formValues.lists.forEach((l: any, idx: number) => {
            if (l.id && l.id.length <= 4) {
                formData.append(`lists[${idx}][id]`, l.id);
            } else {
                formData.append(`lists[${idx}][file]`, l.file);
            }

            formData.append(`lists[${idx}][name]`, l.name);
            if (l.percentage != null) {
                formData.append(`lists[${idx}][percentage]`, l.percentage.toString());
            }
            if (l.expires_at) {
                formData.append(`lists[${idx}][expires_at]`, l.expires_at);
            }
        });

        // 4) Envío (agreementsValidate detecta FormData y pone bien los headers)
        try {
            setLoadingFetching(true);
            const response = await agreementsValidate(formData);
            const { success, warning } = response.data;
            if (success && Array.isArray(warning) && warning.length) {
                setWarnings(warning);
                setShowModal(true);
            } else {
                handleSubmit();
            }
            setLoadingFetching(false);
            return response;
        } catch (ex: any) {
            setLoadingFetching(false);
            console.error(ex.message);
        }
    };

    const handleSubmit = async () => {
        // 1) Construir FormData
        const formData = new FormData();
        formData.append('client_id', clientId as string);
        formData.append('drug_manufacturer_id', formValues.drugManufacturerName.id as string);
        formValues.selectedPharmacy.forEach((id: number) => formData.append('point_of_sale_ids[]', id.toString()));
        formData.append('commercial_agremment_id', formValues.isEditMode ? String(formValues.id) : 'null');

        // Enviar booleano como "1" / "0"
        formData.append('visible_percentage', formValues.isVisiblePercentage ? '1' : '0');

        // 2) Campos "all", filtrando fuera el type_id=3
        formValues.all
            .filter((item: any) => item.type_id !== 3)
            .forEach((item: any, idx: number) => {
                formData.append(`all[${idx}][type_id]`, item.type_id.toString());
                if (item.percentage != null) {
                    formData.append(`all[${idx}][percentage]`, item.percentage.toString());
                }
                if (item.expires_at) {
                    formData.append(`all[${idx}][expires_at]`, item.expires_at);
                }
            });

        // 3) Campos "lists" (incluyendo el File o el id)
        formValues.lists.forEach((l: any, idx: number) => {
            // siempre mando el name
            formData.append(`lists[${idx}][name]`, l.name);

            if (l.id && l.id.length <= 4) {
                formData.append(`lists[${idx}][id]`, l.id);
            } else {
                formData.append(`lists[${idx}][file]`, l.file);
            }

            // campos opcionales
            if (l.percentage != null) {
                formData.append(`lists[${idx}][percentage]`, l.percentage.toString());
            }
            if (l.expires_at) {
                formData.append(`lists[${idx}][expires_at]`, l.expires_at);
            }
        });

        // 4) Envío (agreementsValidate detecta FormData y pone bien los headers)
        try {
            setLoadingFetching(true);
            const response = (await formValues.isEditMode)
                ? updateAgreements(formData, id)
                : saveAgreementsCustom(formData);
            setShowModal(false);
            setLoadingFetching(false);
            if (formValues.isEditMode) {
                toaster.showToaster({
                    type: 'success',
                    message: {
                        title: `Cambios guardados con éxito.`,
                    },
                });
            } else {
                toaster.showToaster({
                    type: 'success',
                    message: {
                        title: `Acuerdo creado con éxito.`,
                    },
                });
            }

            setTimeout(() => {
                push(`/${clientId}/settings/trade-agreements`);
            }, 1000);

            return response;
        } catch (ex: any) {
            setLoadingFetching(false);
            console.error(ex.message);
        }
    };

    const handleDeleteEntry = (recordId: number) => {
    
        setFormValues((prev) => {
            // Buscar si el recordId pertenece a 'all' por type_id
            const isAll = prev.all.some((item) => item.id === recordId || item.type_id === recordId);
    
            const updatedAll = isAll
                ? prev.all.filter((item) => item.id !== recordId && item.type_id !== recordId)
                : prev.all;
    
            const updatedLists = prev.lists.filter((item) => item.id !== recordId);
    
            return {
                ...prev,
                all: updatedAll,
                lists: updatedLists,
            };
        });
    
        setHasChanged(true);
    };

    const isFormValid = () => {
        // Droguería
        if (!formValues.drugManufacturerName.id) return false;

        // Puntos de venta
        if (formValues.selectedPharmacy.length === 0) return false;

        // Productos “all”
        /* if (formValues.all.length === 0) return false;
        for (const item of formValues.all) {
          if (item.percentage == null || item.percentage < 0) return false;
        } */

        // Listados personalizados (si existen)
        /* for (const l of formValues.lists) {
          if (!l.file) return false;
          if (l.name.trim() === '') return false;
          if (l.percentage == null) return false;
        } */

        return true;
    };

    const redirectIfHaveChanges = () => {
        !!hasChanged
            ? show({
                  className: '',
                  cancel: true,
                  size: 'xs',
                  title: '¿Querés descartar los cambios?',
                  body: 'Se perderán los cambios que hiciste.',
                  action: 'Si, descartar',
                  onConfirm: () => push(`/${clientId}/settings/trade-agreements`),
                  onCancel: () => {},
              })
            : push(`/${clientId}/settings/trade-agreements`);
    };

    useEffect(() => {
        // Next.js expone el "pathname" dinámico, no el asPath con los valores reales
        const isEditRoute = pathname === '/[clientId]/settings/trade-agreements/editAgreement/[id]';

        if (isEditRoute) {
            // marcamos modo edición
            setFormValues((prev) => ({
                ...prev,
                isEditMode: true,
            }));
            // si además quieres precargar datos del acuerdo:
            if (id) fetchCommercialAgreement(id as string);
        }
    }, [pathname, id]);

    // Dentro de AgreementForm, antes del return(...)
    const productTypeMap: Record<number, string> = {
        1: 'Medicinales',
        2: 'No medicinales',
        4: 'Refrigerados',
    };

    // Extraemos sólo los type_id que nos interesan (1,2,4),
    // mapeamos al nombre y filtramos posibles undefined
    const selectedProductTypes = formValues.all.map((item) => productTypeMap[item.type_id]).filter(Boolean);

    // Unimos con coma y espacio
    const productType = selectedProductTypes.join(', ');

    return (
        <Layouts>
            <div className={styles.container}>
                <Form layout="vertical" formValue={formValues} onChange={setFormValues}>
                    <Panel className={styles['panel-wrapper']}>
                        <p className={styles['drugManufacturer-title']}>
                            {t('settings.commercialAgreements.sett_titleCommercialAgreements')}
                        </p>
                        <FormInputPicker
                            label="Droguería"
                            isRequired
                            name="drugManufacturerName"
                            data={formattedTest}
                            placeholder="Seleccioná tu droguería"
                            size="lg"
                            value={formValues.drugManufacturerName.id}
                            onChange={(value: string) => {
                                const sel = formattedTest?.find((i) => i.value === +value);
                                setFormValues((prev) => ({
                                    ...prev,
                                    drugManufacturerName: {
                                        id: value,
                                        name: sel?.label || '',
                                    },
                                }));
                                setHasChanged(true);
                            }}
                            error={false}
                            showErrorIcon={false}
                            style={{ width: '100%' }}
                            disabled={isValidating || formValues.isEditMode}
                            showLoaderIconCaret={isValidating}
                        />
                    </Panel>

                    {(formValues.isEditMode || !!formValues.drugManufacturerName.id) && (
                        <>
                            <Panel className={styles['panel-wrapper']}>
                                <div>
                                    <p className={styles['drugManufacturer-title']}>Configuración del acuerdo</p>
                                </div>

                                <div>
                                    <p className={styles['drugManufacturer-title']} style={{ fontSize: '18px' }}>
                                        ¿En qué puntos de venta aplica el acuerdo?
                                    </p>
                                </div>
                                <ExMultiSelectionChecked
                                    data={pos}
                                    inputlabel="Puntos de venta"
                                    value={formValues.selectedPharmacy}
                                    onChange={(val) => {
                                        setFormValues((prev) => ({
                                            ...prev,
                                            selectedPharmacy: val,
                                        }));
                                        setHasChanged(true);
                                    }}
                                    placeholder="Puntos de venta"
                                    className={styles['input-select']}
                                />

                                <div className={styles['divider']}>
                                    <hr />
                                </div>

                                <p className={styles['drugManufacturer-title']}>
                                    ¿Qué productos son parte del acuerdo?
                                </p>
                                {items.map(({ id, label }) => {
                                    const isChecked = formValues.all.some((itm) => itm.type_id === id);
                                    const entry = formValues.all.find((itm) => itm.type_id === id);
                                    return (
                                        <div
                                            key={id}
                                            className={`${styles['container-cards']} ${
                                                isChecked && styles['container-cards-selected']
                                            }`}
                                        >
                                            <div className={styles['card-left']}>
                                                <Checkbox checked={isChecked} onChange={() => handleCheckboxChange(id)}>
                                                    <span className={styles['label']}>{label}</span>
                                                </Checkbox>
                                            </div>
                                            {isChecked && (
                                                <div className={styles['card-right']}>
                                                    <InputGroup className={styles['input-container']}>
                                                        <Input
                                                            size="sm"
                                                            value={entry?.percentage?.toString() || ''}
                                                            onChange={(val) => {
                                                                setFormValues((prev) => ({
                                                                    ...prev,
                                                                    all: prev.all.map((i) =>
                                                                        i.type_id === id
                                                                            ? {
                                                                                  ...i,
                                                                                  percentage: Number(val),
                                                                              }
                                                                            : i,
                                                                    ),
                                                                }));
                                                                setHasChanged(true);
                                                            }}
                                                        />
                                                        <InputGroup.Addon className={styles['addon']}>
                                                            <img src="/assets/images/percent.svg" />
                                                        </InputGroup.Addon>
                                                    </InputGroup>

                                                    <DatePickerForm
                                                        // 1) defaultValue como Date
                                                        defaultValue={
                                                            formValues.lists?.find((l) => l.checkbox === id)?.expires_at
                                                                ? // si ya tengo expires_at, convierto el string a Date
                                                                  dayjs(
                                                                      formValues.lists.find((l) => l.checkbox === id)!
                                                                          .expires_at,
                                                                  ).toDate()
                                                                : // sino, uso hoy
                                                                  ''
                                                        }
                                                        // 2) onChange: saco año, mes y día del objeto Date
                                                        onChange={(date: Date) => {
                                                            const yyyy = date.getFullYear();
                                                            const mm = String(date.getMonth() + 1).padStart(2, '0');
                                                            const dd = String(date.getDate()).padStart(2, '0');
                                                            const formatted = `${yyyy}-${mm}-${dd}`;
                                                            setFormValues((prev) => ({
                                                                ...prev,
                                                                all: prev.all.map((i) =>
                                                                    i.type_id === id
                                                                        ? { ...i, expires_at: formatted }
                                                                        : i,
                                                                ),
                                                            }));
                                                            setHasChanged(true);
                                                        }}
                                                        isExpired={
                                                            formValues.all.find((i) => i.type_id === id)?.isExpired
                                                        }
                                                        placement="bottom"
                                                        onClean={() =>
                                                            setFormValues((prev) => ({
                                                                ...prev,
                                                                all: prev.all.map((i) =>
                                                                    i.type_id === id ? { ...i, expires_at: null } : i,
                                                                ),
                                                            }))
                                                        }
                                                    />

                                                    <div className={styles['icon-trash-container']}>
                                                        <IconButton
                                                            onClick={() => handleDeleteEntry(id)}
                                                            style={{ width: 24, height: 24, background: 'transparent' }}
                                                            icon={
                                                                <img
                                                                    src="/assets/images/delete_trash.svg"
                                                                    className={styles['icon-trash']}
                                                                />
                                                            }
                                                        />
                                                    </div>
                                                </div>
                                            )}
                                        </div>
                                    );
                                })}

                                {/* Listado personalizado (id=3) y subida de archivo */}
                                <div
                                    className={`${styles['container-cards-lists']} ${
                                        !!formValues.lists[0] && styles['container-cards-lists-selected']
                                    }`}
                                >
                                    <Checkbox checked={!!formValues.lists[0]} onChange={() => handleCheckboxChange(3)}>
                                        <div className={styles['container-custom-checkbox']}>
                                            <span className={styles['label']}>Listado personalizado</span>
                                            <span>{`${
                                                !!formValues.lists[0]
                                                    ? 'Agregá una lista con el mismo formato que usas para iniciar una compra eficiente.'
                                                    : 'Subí un archivo .txt con los productos del acuerdo.'
                                            }`}</span>
                                        </div>
                                    </Checkbox>

                                    {formValues.lists.map((entry, idx) => {
                                        return (
                                            <div key={entry.id} className={styles['wrapper-lists']}>
                                                <div style={{ position: 'relative', width: '100%', marginRight: 8 }}>
                                                    <InputGroup>
                                                        <Input
                                                            maxLength={18}
                                                            size="lg"
                                                            placeholder="Nombre del acuerdo"
                                                            value={entry.name}
                                                            onChange={(val) => {
                                                                setFormValues((prev) => ({
                                                                    ...prev,
                                                                    lists: prev.lists.map((l) =>
                                                                        l.id === entry.id ? { ...l, name: val } : l,
                                                                    ),
                                                                }));
                                                                setHasChanged(true);
                                                            }}
                                                        />
                                                    </InputGroup>
                                                    <div className={styles['file-info-absolute']}>
                                                        <span className={styles['filename']}>
                                                            {entry?.file?.name || formValues?.lists[0]?.file}
                                                        </span>
                                                        <span className={styles['file-count']}>
                                                            {entry.name?.length || 0}/18
                                                        </span>
                                                    </div>
                                                </div>

                                                <InputGroup className={styles['input-container-percent']}>
                                                    <Input
                                                        size="sm"
                                                        min={0}
                                                        max={100}
                                                        value={entry?.percentage?.toString() || ''}
                                                        onChange={(val) => {
                                                            setFormValues((prev) => {
                                                                const lists = prev.lists.map((l) =>
                                                                    l.id === entry.id
                                                                        ? { ...l, percentage: Number(val) }
                                                                        : l,
                                                                );
                                                                const all = prev.all.map((i) =>
                                                                    i.id === entry.id
                                                                        ? { ...i, percentage: Number(val) }
                                                                        : i,
                                                                );
                                                                return { ...prev, lists, all };
                                                            });
                                                            setHasChanged(true);
                                                        }}
                                                    />
                                                    <InputGroup.Addon className={styles['addon']}>
                                                        <img src="/assets/images/percent.svg" />
                                                    </InputGroup.Addon>
                                                </InputGroup>

                                                <DatePickerForm
                                                    // 1) defaultValue como Date local
                                                    defaultValue={(() => {
                                                        const existing = formValues.lists.find(
                                                            (l) => l.checkbox === entry.checkbox,
                                                        )?.expires_at;
                                                        return existing ? dayjs(existing, 'YYYY-MM-DD').toDate() : '';
                                                    })()}
                                                    // 2) onChange: extraemos año, mes y día para construir YYYY-MM-DD
                                                    onChange={(date: Date) => {
                                                        const yyyy = date.getFullYear();
                                                        const mm = String(date.getMonth() + 1).padStart(2, '0');
                                                        const dd = String(date.getDate()).padStart(2, '0');
                                                        const formatted = `${yyyy}-${mm}-${dd}`;

                                                        setFormValues((prev) => {
                                                            const lists = prev.lists.map((l) =>
                                                                l.checkbox === entry.checkbox
                                                                    ? { ...l, expires_at: formatted }
                                                                    : l,
                                                            );
                                                            const all = prev.all.map((i) =>
                                                                i.type_id === 3 ? { ...i, expires_at: formatted } : i,
                                                            );
                                                            return { ...prev, lists, all };
                                                        });
                                                        setHasChanged(true);
                                                    }}
                                                    isExpired={
                                                        formValues.lists.find((l) => l.checkbox === entry.checkbox)
                                                            ?.isExpired
                                                    }
                                                    placement="bottom"
                                                    onClean={() => {
                                                        setFormValues((prev) => {
                                                            const lists = prev.lists.map((l) =>
                                                                l.checkbox === entry.checkbox
                                                                    ? { ...l, expires_at: null }
                                                                    : l,
                                                            );
                                                            const all = prev.all.map((i) =>
                                                                i.type_id === 3 ? { ...i, expires_at: null } : i,
                                                            );
                                                            return { ...prev, lists, all };
                                                        });
                                                    }}
                                                />

                                                <div className={styles['icon-trash-container']}>
                                                    <IconButton
                                                        style={{ width: 24, height: 24, background: 'transparent' }}
                                                        icon={
                                                            <img
                                                                src="/assets/images/delete_trash.svg"
                                                                className={styles['icon-trash']}
                                                            />
                                                        }
                                                        onClick={() => handleDeleteEntry(entry?.id)}
                                                    />
                                                </div>
                                            </div>
                                        );
                                    })}
                                    <div style={{ marginTop: 8 }}>
                                        <input
                                            type="file"
                                            ref={fileInputRef}
                                            style={{ display: 'none' }}
                                            onChange={handleFileChange}
                                        />
                                        <div style={{ marginTop: 8 }}>
                                            <ExButton
                                                appearance="ghost"
                                                IconComponent={<img src="/assets/images/add.svg" />}
                                                onClick={handleAddCustomList}
                                            >
                                                Agregar archivo
                                            </ExButton>
                                        </div>
                                    </div>
                                </div>
                            </Panel>

                            <div className={styles['panel-wrapper-toggle']}>
                                <div className={styles['comparative-row']}>
                                    <div className={styles['comparative-left']}>
                                        <Toggle
                                            size="md"
                                            checked={formValues.isVisiblePercentage}
                                            onChange={(value: boolean) => {
                                                setFormValues((prev) => ({
                                                    ...prev,
                                                    isVisiblePercentage: value,
                                                }));
                                                setHasChanged(true);
                                            }}
                                        />
                                        <span className={styles['label']}>Mostrar % del acuerdo en la comparativa</span>
                                    </div>

                                    <div className={styles['comparative-right']}>
                                        <div className={styles['gray-box']}>
                                            <div className={styles['percentage-badge']}>2%</div>
                                            <div className={styles['gray-box-content']}>
                                                <input type="radio" checked={false} />
                                                <span className={styles['price-text']}>$ xxxxx</span>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            </div>

                            <div className={styles.footer}>
                                <ExButton appearance="ghost" onClick={() => redirectIfHaveChanges()}>
                                    Cancelar
                                </ExButton>
                                <ExButton
                                    appearance="primary"
                                    onClick={handleValidation}
                                    loading={loadingFetching}
                                    disabled={loadingFetching || !isFormValid()}
                                >
                                    {formValues.isEditMode ? 'Guardar cambios' : 'Crear acuerdo'}
                                </ExButton>
                            </div>
                        </>
                    )}
                </Form>
            </div>
            <InformationModal
                open={showModal}
                setShowModal={setShowModal}
                drugmanufacturerName={formValues?.drugManufacturerName?.name}
                productType={productType}
                submit={handleSubmit}
                warning={warnings}
            />
        </Layouts>
    );
};

export default AgreementForm;
